// Crew Mode pilot shell v0 — bundle C (minimal telemetry).
//
// A deliberately thin wrapper over the existing `src/utils/log.ts` event
// log (tenant-scoped localStorage ring log). Crew Mode adds NO new
// telemetry backend, event schema, or instrumentation depth — that is an
// explicit Charter non-goal and PRD §4 exclusion. This module only:
//   1. namespaces Crew Mode events under `crew.*`, and
//   2. fixes the small, PII-safe `details` shape callers may attach.
//
// `details` carries at most: the stream name, the farm id, the resulting
// sync state, and the `capturedByActorId` that is already stamped on every
// captured row (Charter D-4). It MUST NEVER carry a captured measurement
// value (count, weight, parameter reading, feed kg) — PRD §11 AC #4.

import { logEvent } from '../../utils/log'

export type CrewEventDetails = {
  stream?: 'water' | 'feed' | 'mortality' | 'weightSample'
  farmId?: string
  // The four-state sync outcome of a capture, mirrored from the capture
  // screen's own badge state. Not a derived figure — just which of the
  // fixed states the row landed in.
  syncState?: 'local' | 'pending' | 'synced' | 'error'
  // Already present on every captured row (Charter D-4). Included so the
  // §13 gate metrics can be attributed per operator; it is not new PII.
  capturedByActorId?: string
}

// Event names whose suffix marks a failure — these are logged at the
// `'error'` level instead of `'action'` so they surface in the same place
// as other Crew Mode failures. Everything else is an `'action'`.
const isFailureEventName = (name: string): boolean => name.endsWith('.error') || name.endsWith('.failed')

/**
 * Record a Crew Mode lifecycle event. `name` is the un-namespaced event
 * (e.g. `home.opened`, `capture.start`, `capture.confirm`, `capture.error`,
 * `operator.switch`); it is stored as `crew.<name>`.
 *
 * Writes synchronously to tenant-scoped localStorage via `logEvent` and
 * never awaits a network call (Charter O-5). Failures to write are
 * swallowed — telemetry must never block or break a capture flow.
 */
export const recordCrewEvent = (name: string, details?: CrewEventDetails): void => {
  try {
    logEvent(isFailureEventName(name) ? 'error' : 'action', `crew.${name}`, details)
  } catch {
    // ignore — a telemetry write must never surface to the operator
  }
}
